import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  KeyIcon,
  CheckCircleIcon,
  XCircleIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import { campaignService } from '../../services/campaignService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Button from '../../components/UI/Button';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/UI/Card';
import { SectionHeader } from '../../components/UI/SectionHeader';
import toast from 'react-hot-toast';
import ErrorMessage from '../../components/UI/ErrorMessage';

const openaiSettingsSchema = z.object({
  apiKey: z.string().min(1, 'OpenAI API key is required').regex(/^sk-/, 'API key must start with "sk-"'),
});

const telnyxSettingsSchema = z.object({
  apiKey: z.string().min(1, 'Telnyx API Key is required').regex(/^KEY/, 'API key must start with "KEY"'),
  connectionId: z.string().min(1, 'Connection ID is required'),
});

const vapiSettingsSchema = z.object({
  publicApiKey: z
    .string()
    .min(1, 'Public API key is required')
    .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, 'Must be a valid UUID'),
  privateApiKey: z
    .string()
    .min(1, 'Private API key is required')
    .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, 'Must be a valid UUID'),
});

type OpenAISettingsFormData = z.infer<typeof openaiSettingsSchema>;
type TelnyxSettingsFormData = z.infer<typeof telnyxSettingsSchema>;
type VapiSettingsFormData = z.infer<typeof vapiSettingsSchema>;

interface OpenAISettings {
  maskedApiKey?: string;
  hasApiKey: boolean;
  isConnected: boolean;
  lastTestedAt?: string;
}

interface TelnyxSettings {
  maskedApiKey?: string;
  maskedConnectionId?: string;
  hasApiKey: boolean;
  isConnected: boolean;
  lastTestedAt?: string;
}

interface VapiSettings {
  maskedPublicApiKey?: string;
  maskedPrivateApiKey?: string;
  hasApiKey: boolean;
  isConnected: boolean;
  lastTestedAt?: string;
}



const SettingsPage: React.FC = () => {
  const [openaiSettings, setOpenaiSettings] = useState<OpenAISettings | null>(null);
  const [telnyxSettings, setTelnyxSettings] = useState<TelnyxSettings | null>(null);
  const [vapiSettings, setVapiSettings] = useState<VapiSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [testingOpenAIConnection, setTestingOpenAIConnection] = useState(false);
  const [testingTelnyxConnection, setTestingTelnyxConnection] = useState(false);
  const [testingVapiConnection, setTestingVapiConnection] = useState(false);
  const [showOpenAIApiKey, setShowOpenAIApiKey] = useState(false);
  const [showTelnyxApiKey, setShowTelnyxApiKey] = useState(false);
  const [showVapiPublicKey, setShowVapiPublicKey] = useState(false);
  const [showVapiPrivateKey, setShowVapiPrivateKey] = useState(false);
  const [disconnectingOpenAI, setDisconnectingOpenAI] = useState(false);
  const [disconnectingTelnyx, setDisconnectingTelnyx] = useState(false);
  const [disconnectingVapi, setDisconnectingVapi] = useState(false);

  const {
    register: registerOpenAI,
    handleSubmit: handleSubmitOpenAI,
    formState: { errors: openaiErrors, isSubmitting: isSubmittingOpenAI },
    reset: resetOpenAI,
  } = useForm<OpenAISettingsFormData>({
    resolver: zodResolver(openaiSettingsSchema),
  });

  const {
    register: registerTelnyx,
    handleSubmit: handleSubmitTelnyx,
    formState: { errors: telnyxErrors, isSubmitting: isSubmittingTelnyx },
    reset: resetTelnyx,
  } = useForm<TelnyxSettingsFormData>({
    resolver: zodResolver(telnyxSettingsSchema),
  });

  const {
    register: registerVapi,
    handleSubmit: handleSubmitVapi,
    formState: { errors: vapiErrors, isSubmitting: isSubmittingVapi },
    reset: resetVapi,
  } = useForm<VapiSettingsFormData>({
    resolver: zodResolver(vapiSettingsSchema),
  });

  useEffect(() => {
    fetchOpenAISettings();
    fetchTelnyxSettings();
    fetchVapiSettings();
    setLoading(false);
  }, []);

  const fetchOpenAISettings = async () => {
    try {
      const settings = await campaignService.getOpenAISettings();
      setOpenaiSettings(settings);
      
      // Reset form with fetched values
      if (settings.hasApiKey && settings.maskedApiKey) {
        resetOpenAI({
          apiKey: settings.maskedApiKey,
        });
      }
    } catch (error) {
      console.error('Failed to fetch OpenAI settings:', error);
    }
  };

  const fetchTelnyxSettings = async () => {
    try {
      const settings = await campaignService.getTelnyxSettings();
      setTelnyxSettings(settings);

      if (settings.hasApiKey) {
        resetTelnyx({
          apiKey: settings.maskedApiKey || '',
          connectionId: settings.maskedConnectionId || '',
        });
      }
    } catch (error) {
      console.error('Failed to fetch Telnyx settings:', error);
    }
  };

  const fetchVapiSettings = async () => {
    try {
      const settings = await campaignService.getVapiSettings();
      setVapiSettings(settings);

      if (settings.hasApiKey) {
        resetVapi({
          publicApiKey: settings.maskedPublicApiKey || '',
          privateApiKey: settings.maskedPrivateApiKey || '',
        });
      }
    } catch (error) {
      console.error('Failed to fetch VAPI settings:', error);
    }
  };



  const onSubmitOpenAI = async (data: OpenAISettingsFormData) => {
    try {
      await campaignService.saveOpenAISettings(data);
      await fetchOpenAISettings();
      toast.success('OpenAI settings saved successfully!');
    } catch (error) {
      console.error('Failed to save OpenAI settings:', error);
      toast.error('Failed to save OpenAI settings');
    }
  };

  const onSubmitTelnyx = async (data: TelnyxSettingsFormData) => {
    try {
      await campaignService.saveTelnyxSettings(data);
      await fetchTelnyxSettings();
      toast.success('Telnyx settings saved successfully');
    } catch (error) {
      console.error('Failed to save Telnyx settings:', error);
      toast.error('Failed to save Telnyx settings');
    }
  };

  const onSubmitVapi = async (data: VapiSettingsFormData) => {
    try {
      await campaignService.saveVapiSettings(data);
      await fetchVapiSettings();
      toast.success('VAPI settings saved successfully');
    } catch (error) {
      console.error('Failed to save VAPI settings:', error);
      toast.error('Failed to save VAPI settings');
    }
  };

  const testOpenAIConnection = async () => {
    try {
      setTestingOpenAIConnection(true);
      const result = await campaignService.testOpenAIConnection();
      
      if (result.success) {
        toast.success('OpenAI connection successful!');
        await fetchOpenAISettings();
      } else {
        toast.error(result.message || 'Connection test failed');
      }
    } catch (error) {
      console.error('Failed to test OpenAI connection:', error);
      toast.error('Failed to test connection');
    } finally {
      setTestingOpenAIConnection(false);
    }
  };

  const testTelnyxConnection = async () => {
    try {
      setTestingTelnyxConnection(true);
      const result = await campaignService.testTelnyxConnection();

      if (result.success) {
        toast.success('Telnyx connection successful!');
        await fetchTelnyxSettings();
      } else {
        toast.error(result.message || 'Telnyx connection failed');
      }
    } catch (error) {
      console.error('Failed to test Telnyx connection:', error);
      toast.error('Failed to test Telnyx connection');
    } finally {
      setTestingTelnyxConnection(false);
    }
  };

  const testVapiConnection = async () => {
    try {
      setTestingVapiConnection(true);
      const result = await campaignService.testVapiConnection();

      if (result.success) {
        toast.success('VAPI connection successful!');
        await fetchVapiSettings();
      } else {
        toast.error(result.message || 'VAPI connection failed');
      }
    } catch (error) {
      console.error('Failed to test VAPI connection:', error);
      toast.error('Failed to test VAPI connection');
    } finally {
      setTestingVapiConnection(false);
    }
  };

  const disconnectOpenAI = async () => {
    if (!window.confirm('Are you sure you want to disconnect OpenAI? This will remove your stored API key.')) {
      return;
    }

    try {
      setDisconnectingOpenAI(true);
      await campaignService.disconnectOpenAI();
      toast.success('OpenAI disconnected successfully');
      await fetchOpenAISettings();
      resetOpenAI({ apiKey: '' });
    } catch (error) {
      toast.error('Failed to disconnect OpenAI');
    } finally {
      setDisconnectingOpenAI(false);
    }
  };

  const disconnectTelnyx = async () => {
    if (!window.confirm('Are you sure you want to disconnect Telnyx? This will remove your stored credentials.')) {
      return;
    }

    try {
      setDisconnectingTelnyx(true);
      await campaignService.disconnectTelnyx();
      toast.success('Telnyx disconnected successfully');
      await fetchTelnyxSettings();
      resetTelnyx({ apiKey: '', connectionId: '' });
    } catch (error) {
      toast.error('Failed to disconnect Telnyx');
    } finally {
      setDisconnectingTelnyx(false);
    }
  };

  const disconnectVapi = async () => {
    if (!window.confirm('Are you sure you want to disconnect VAPI? This will remove your stored API keys.')) {
      return;
    }

    try {
      setDisconnectingVapi(true);
      await campaignService.disconnectVapi();
      toast.success('VAPI disconnected successfully');
      await fetchVapiSettings();
      resetVapi({ publicApiKey: '', privateApiKey: '' });
    } catch (error) {
      toast.error('Failed to disconnect VAPI');
    } finally {
      setDisconnectingVapi(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="mb-8">
        <CardContent className="p-6">
          <h1 className="text-2xl font-bold mb-2 text-foreground">Settings</h1>
          <p className="text-muted-foreground">Manage your account and integration settings</p>
        </CardContent>
      </Card>

      {/* OpenAI Integration */}
      <Card>
        <CardContent className="px-4 py-5 sm:p-6">
          <SectionHeader
            title="OpenAI Integration"
            description="Connect your OpenAI account for AI-powered conversation capabilities."
          />

          {/* Connection Status */}
          {openaiSettings && (
            <div className="mt-4 flex items-center">
              {openaiSettings.isConnected ? (
                <div className="flex items-center text-green-600">
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  <span className="text-sm font-medium">Connected</span>
                  {openaiSettings.lastTestedAt && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      Last tested: {new Date(openaiSettings.lastTestedAt).toLocaleString()}
                    </span>
                  )}
                </div>
              ) : (
                <div className="flex items-center text-red-600">
                  <XCircleIcon className="h-5 w-5 mr-2" />
                  <span className="text-sm font-medium">
                    {openaiSettings.hasApiKey ? 'Connection Failed' : 'Not Connected'}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* API Key Form */}
          <form onSubmit={handleSubmitOpenAI(onSubmitOpenAI)} className="mt-6 space-y-4">
            <div>
              <label htmlFor="openaiApiKey" className="block text-sm font-medium text-foreground">
                OpenAI API Key
              </label>
              <div className="mt-1 relative">
                <input
                  {...registerOpenAI('apiKey')}
                  type={showOpenAIApiKey ? 'text' : 'password'}
                  className="input"
                  placeholder="Enter your OpenAI API key (sk-...)"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center h-auto"
                  onClick={() => setShowOpenAIApiKey(!showOpenAIApiKey)}
                >
                  <span className="text-sm text-muted-foreground">
                    {showOpenAIApiKey ? 'Hide' : 'Show'}
                  </span>
                </Button>
              </div>
              <ErrorMessage message={openaiErrors.apiKey?.message} />
              <div className="mt-1 text-xs text-muted-foreground">
                <p>Used for AI conversation generation and processing</p>
              </div>
            </div>

            <div className="mt-4 text-xs text-muted-foreground space-y-2">
              <p>
                Your API key is encrypted and stored securely. Get your key from{' '}
                <a
                  href="https://platform.openai.com/api-keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-success hover:text-success/80"
                >
                  OpenAI Platform
                </a>
              </p>
              <div className="bg-muted/50 border border-border rounded-md p-2">
                <p className="text-foreground text-xs">
                  ℹ️ <strong>Note:</strong> Make sure your OpenAI account has sufficient credits and API access enabled.
                </p>
              </div>
            </div>

            <div className="flex space-x-3">
              <Button
                type="submit"
                variant="primary"
                size="sm"
                loading={isSubmittingOpenAI}
              >
                Save API Key
              </Button>

              {openaiSettings?.hasApiKey && (
                <>
                  <Button
                    type="button"
                    onClick={testOpenAIConnection}
                    variant="primary"
                    size="sm"
                    loading={testingOpenAIConnection}
                  >
                    Test Connection
                  </Button>

                  <Button
                    type="button"
                    onClick={disconnectOpenAI}
                    variant="destructive"
                    size="sm"
                    loading={disconnectingOpenAI}
                  >
                    Disconnect
                  </Button>
                </>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Telnyx Integration */}
      <Card>
        <CardContent className="px-4 py-5 sm:p-6">
          <SectionHeader
            title="Telnyx Integration"
            description="Connect your Telnyx account for Call Control API with native AMD (Answering Machine Detection)."
          />

          {/* Connection Status */}
          {telnyxSettings && (
            <div className="mt-4 flex items-center">
              {telnyxSettings.isConnected ? (
                <div className="flex items-center text-success">
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  <span className="text-sm font-medium">Connected</span>
                  {telnyxSettings.lastTestedAt && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      Last tested: {new Date(telnyxSettings.lastTestedAt).toLocaleString()}
                    </span>
                  )}
                </div>
              ) : (
                <div className="flex items-center text-destructive">
                  <XCircleIcon className="h-5 w-5 mr-2" />
                  <span className="text-sm font-medium">
                    {telnyxSettings.hasApiKey ? 'Connection Failed' : 'Not Connected'}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Telnyx API Key Form */}
          <form onSubmit={handleSubmitTelnyx(onSubmitTelnyx)} className="mt-6 space-y-4">
            <div>
              <label htmlFor="telnyxApiKey" className="block text-sm font-medium text-foreground">
                Telnyx API Key
              </label>
              <div className="mt-1 relative">
                <input
                  {...registerTelnyx('apiKey')}
                  type={showTelnyxApiKey ? 'text' : 'password'}
                  className="input"
                  placeholder="Enter your Telnyx API key (KEY...)"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center h-auto"
                  onClick={() => setShowTelnyxApiKey(!showTelnyxApiKey)}
                >
                  <span className="text-sm text-muted-foreground">
                    {showTelnyxApiKey ? 'Hide' : 'Show'}
                  </span>
                </Button>
              </div>
              <ErrorMessage message={telnyxErrors.apiKey?.message} />
            </div>

            <div>
              <label htmlFor="telnyxConnectionId" className="block text-sm font-medium text-foreground">
                Connection ID
              </label>
              <div className="mt-1">
                <input
                  {...registerTelnyx('connectionId')}
                  type="text"
                  className="input"
                  placeholder="Enter your Telnyx Connection ID"
                />
              </div>
              <ErrorMessage message={telnyxErrors.connectionId?.message} />
              <div className="mt-1 text-xs text-muted-foreground">
                <p>The Call Control Connection ID from your Telnyx portal</p>
              </div>
            </div>

            <div className="mt-4 text-xs text-muted-foreground space-y-2">
              <p>
                Your API key is encrypted and stored securely. Get your key and connection ID from{' '}
                <a
                  href="https://portal.telnyx.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-success hover:text-success/80"
                >
                  Telnyx Portal
                </a>
              </p>
              <div className="bg-muted/50 border border-border rounded-md p-2">
                <p className="text-foreground text-xs">
                  ℹ️ <strong>Note:</strong> Outbound calls use Telnyx <code>premium</code> Answering Machine Detection (ML-based). It classifies human residence/business vs machine/fax/silence more accurately than the free <code>detect_words</code> mode, and is billed per call on Telnyx. Rollback: <code>TELNYX_AMD_MODE=detect_words</code>.
                </p>
              </div>
            </div>

            <div className="flex space-x-3">
              <Button
                type="submit"
                variant="primary"
                size="sm"
                loading={isSubmittingTelnyx}
              >
                Save Telnyx Settings
              </Button>

              {telnyxSettings?.hasApiKey && (
                <>
                  <Button
                    type="button"
                    onClick={testTelnyxConnection}
                    variant="primary"
                    size="sm"
                    loading={testingTelnyxConnection}
                  >
                    Test Connection
                  </Button>

                  <Button
                    type="button"
                    onClick={disconnectTelnyx}
                    variant="destructive"
                    size="sm"
                    loading={disconnectingTelnyx}
                  >
                    Disconnect
                  </Button>
                </>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* VAPI Integration */}
      <Card>
        <CardContent className="px-4 py-5 sm:p-6">
          <SectionHeader
            title="VAPI Integration"
            description="Connect your VAPI account for AI voice bot campaigns (VAPI Direct and VAPI Bridge)."
          />

          {vapiSettings && (
            <div className="mt-4 flex items-center">
              {vapiSettings.isConnected ? (
                <div className="flex items-center text-success">
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  <span className="text-sm font-medium">Connected</span>
                  {vapiSettings.lastTestedAt && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      Last updated: {new Date(vapiSettings.lastTestedAt).toLocaleString()}
                    </span>
                  )}
                </div>
              ) : (
                <div className="flex items-center text-destructive">
                  <XCircleIcon className="h-5 w-5 mr-2" />
                  <span className="text-sm font-medium">
                    {vapiSettings.hasApiKey ? 'Connection Failed' : 'Not Connected'}
                  </span>
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleSubmitVapi(onSubmitVapi)} className="mt-6 space-y-4">
            <div>
              <label htmlFor="vapiPublicApiKey" className="block text-sm font-medium text-foreground">
                Public API Key
              </label>
              <div className="mt-1 relative">
                <input
                  {...registerVapi('publicApiKey')}
                  type={showVapiPublicKey ? 'text' : 'password'}
                  className="input"
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center h-auto"
                  onClick={() => setShowVapiPublicKey(!showVapiPublicKey)}
                >
                  <span className="text-sm text-muted-foreground">
                    {showVapiPublicKey ? 'Hide' : 'Show'}
                  </span>
                </Button>
              </div>
              <ErrorMessage message={vapiErrors.publicApiKey?.message} />
            </div>

            <div>
              <label htmlFor="vapiPrivateApiKey" className="block text-sm font-medium text-foreground">
                Private API Key
              </label>
              <div className="mt-1 relative">
                <input
                  {...registerVapi('privateApiKey')}
                  type={showVapiPrivateKey ? 'text' : 'password'}
                  className="input"
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center h-auto"
                  onClick={() => setShowVapiPrivateKey(!showVapiPrivateKey)}
                >
                  <span className="text-sm text-muted-foreground">
                    {showVapiPrivateKey ? 'Hide' : 'Show'}
                  </span>
                </Button>
              </div>
              <ErrorMessage message={vapiErrors.privateApiKey?.message} />
            </div>

            <div className="mt-4 text-xs text-muted-foreground space-y-2">
              <p>
                Keys are encrypted and stored securely. Get both keys from{' '}
                <a
                  href="https://dashboard.vapi.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-success hover:text-success/80"
                >
                  VAPI Dashboard
                </a>
                {' '}→ API Keys. Use the exact <strong>Public</strong> and <strong>Private</strong> labels from VAPI (do not swap).
              </p>
              <div className="bg-muted/50 border border-border rounded-md p-2">
                <p className="text-foreground text-xs">
                  <strong>Public key</strong> is used for server API calls. <strong>Private key</strong> is stored for webhooks. Also add OpenAI in VAPI Dashboard → Integrations. Set <code>VAPI_WEBHOOK_URL</code> in backend <code>.env</code>.
                </p>
              </div>
            </div>

            <div className="flex space-x-3">
              <Button
                type="submit"
                variant="primary"
                size="sm"
                loading={isSubmittingVapi}
              >
                Save VAPI Settings
              </Button>

              {vapiSettings?.hasApiKey && (
                <>
                  <Button
                    type="button"
                    onClick={testVapiConnection}
                    variant="primary"
                    size="sm"
                    loading={testingVapiConnection}
                  >
                    Test Connection
                  </Button>

                  <Button
                    type="button"
                    onClick={disconnectVapi}
                    variant="destructive"
                    size="sm"
                    loading={disconnectingVapi}
                  >
                    Disconnect
                  </Button>
                </>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* General Settings */}
      <Card>
        <CardContent className="px-4 py-5 sm:p-6">
          <SectionHeader
            title="General Settings"
            description="Configure general application settings and preferences."
          />

          <div className="mt-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-foreground">
                    Email Notifications
                  </label>
                  <p className="text-sm text-muted-foreground">
                    Receive email notifications for campaign updates and results.
                  </p>
                </div>
                <div className="switch-group">
                  <input
                    type="checkbox"
                    id="emailNotifications"
                    defaultChecked
                    className="switch"
                  />
                  <label htmlFor="emailNotifications" className="sr-only">
                    Toggle email notifications
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-foreground">
                    Auto-pause on Errors
                  </label>
                  <p className="text-sm text-muted-foreground">
                    Automatically pause campaigns when multiple call failures occur.
                  </p>
                </div>
                <div className="switch-group">
                  <input
                    type="checkbox"
                    id="autoPauseErrors"
                    defaultChecked
                    className="switch"
                  />
                  <label htmlFor="autoPauseErrors" className="sr-only">
                    Toggle auto-pause on errors
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-foreground">
                    Data Retention
                  </label>
                  <p className="text-sm text-muted-foreground">
                    How long to keep call recordings and transcripts.
                  </p>
                </div>
                <select 
                  className="block px-3 py-2 border border-border rounded-lg shadow-sm bg-background text-foreground focus:ring-2 focus:ring-ring focus:border-ring sm:text-sm transition-colors duration-150"
                  defaultValue="90"
                >
                  <option value="30">30 days</option>
                  <option value="90">
                    90 days
                  </option>
                  <option value="180">180 days</option>
                  <option value="365">1 year</option>
                </select>
              </div>
            </div>

            <div className="mt-6">
              <button
                type="button"
                className="btn btn-primary btn-sm"
              >
                Save Settings
              </button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;
